import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeftIcon, LayersIcon } from 'lucide-react'

import { EmptyState } from '@ems/ui/patterns/empty-state'
import { PageHeader } from '@ems/ui/patterns/page-header'

import { NotFoundView } from '#admin/components/not-found-view.tsx'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { hasPlatformScope } from '#admin/features/platform/api.ts'
import { SeriesDetailSkeleton } from '#admin/features/platform/platform-skeletons.tsx'
import { seriesDetailQuery } from '#admin/features/platform/series/queries.ts'
import { SeriesComparison } from '#admin/features/platform/series/series-comparison.tsx'
import { TemplateCard } from '#admin/features/platform/series/template-card.tsx'

/** `/platform/series/$seriesId` — the editions of one series compared, and its template. */
export const Route = createFileRoute('/_authenticated/platform/series/$seriesId')({
  pendingComponent: SeriesDetailSkeleton,
  staticData: { crumb: useSeriesCrumb },
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(seriesDetailQuery(params.seriesId)),
  notFoundComponent: NotFoundView,
  component: SeriesPage,
})

function useSeriesCrumb(): string {
  const { seriesId } = Route.useParams()
  return useQuery(seriesDetailQuery(seriesId)).data?.name ?? 'Series'
}

function SeriesPage() {
  const { seriesId } = Route.useParams()
  const { data: series } = useSuspenseQuery(seriesDetailQuery(seriesId))
  const { data: session } = useSuspenseQuery(sessionQuery())
  const canManage = hasPlatformScope(session, 'platform:tenants.manage')

  return (
    <>
      <div className="flex flex-col gap-4">
        <Link
          to="/platform/series"
          className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1 text-sm"
        >
          <ArrowLeftIcon className="size-4" />
          Series
        </Link>
        <PageHeader
          title={series.name}
          description={
            <>
              <span className="font-mono">{series.slug}</span> · {series.editions} edition
              {series.editions === 1 ? '' : 's'}
            </>
          }
        />
      </div>

      {series.editionList.length === 0 ? (
        <EmptyState
          icon={<LayersIcon />}
          title="No editions yet"
          description="Provision an edition into this series to compare it here."
        />
      ) : (
        <SeriesComparison editions={series.editionList} />
      )}

      <TemplateCard key={series.template?.id ?? 'none'} series={series} canManage={canManage} />
    </>
  )
}
